import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { useReadProjectFile } from "@vorloop/api-client-react";
import { useYjsMonaco } from "../../hooks/useYjsMonaco";
import { IconX } from "../layout/Icons";

// y-monaco needs the real Monaco editor instance; we accept it as `any`
// from @monaco-editor/react and let the binding deal with it.
type MonacoEditorInstance = {
  onDidChangeCursorPosition: (cb: (e: { position: { lineNumber: number; column: number } }) => void) => void;
  addCommand: (keybinding: number, handler: () => void) => string | null;
  getValue: () => string;
  getModel: () => unknown;
};

const MonacoEditor = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default })),
);

const LANGUAGE_BY_EXT: Record<string, string> = {
  ts: "typescript",
  tsx: "typescript",
  js: "javascript",
  jsx: "javascript",
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  md: "markdown",
  css: "css",
  html: "html",
  py: "python",
  rs: "rust",
};

const FRIENDLY_LANG: Record<string, string> = {
  typescript: "TypeScript",
  javascript: "JavaScript",
  json: "JSON",
  yaml: "YAML",
  markdown: "Markdown",
  css: "CSS",
  html: "HTML",
  python: "Python",
  rust: "Rust",
  plaintext: "Plain Text",
};

function detectLanguage(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase();
  return ext && LANGUAGE_BY_EXT[ext] ? LANGUAGE_BY_EXT[ext] : "plaintext";
}

export interface FileEditorProps {
  readonly projectId: string;
  readonly path: string | null;
  readonly onClose?: () => void;
}

export function FileEditor({ projectId, path, onClose }: FileEditorProps) {
  const { data, isLoading, error } = useReadProjectFile(projectId, path);
  const language = useMemo(() => (path ? detectLanguage(path) : "plaintext"), [path]);
  const [cursor, setCursor] = useState({ line: 1, column: 1 });
  const editorRef = useRef<MonacoEditorInstance | null>(null);
  const [editor, setEditor] = useState<MonacoEditorInstance | null>(null);

  // Wire the Y.js / WebSocket co-edit binding. Persistence happens on
  // the server (debounced ProjectSource.write), so no local save logic
  // is needed here anymore.
  const co = useYjsMonaco({ projectId, filePath: path, editor });
  const coStatus = co.state;

  if (!path) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "grid",
          placeItems: "center",
          color: "var(--text-tertiary)",
          fontSize: 12,
          padding: 24,
          textAlign: "center",
        }}
      >
        Select a file in the Library to open it.
      </div>
    );
  }

  const filename = path.split("/").pop() ?? path;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        background: "#0F1115",
      }}
    >
      <TabStrip filename={filename} fullPath={path} coStatus={coStatus} onClose={onClose} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <Message>Loading file…</Message>
        ) : error ? (
          <Message error>{error.message}</Message>
        ) : (
          <Suspense fallback={<Message>Loading editor…</Message>}>
            <MonacoEditor
              height="100%"
              theme="vorloop-dark"
              language={language}
              // Initial value comes from the server fetch; the Y.js
              // binding takes over once the WebSocket lands and may
              // replace this content with the canonical Y.Doc state.
              defaultValue={data?.content ?? ""}
              beforeMount={(monaco) => {
                monaco.editor.defineTheme("vorloop-dark", {
                  base: "vs-dark",
                  inherit: true,
                  rules: [
                    { token: "comment", foreground: "6B7280", fontStyle: "italic" },
                    { token: "keyword", foreground: "C084FC" },
                    { token: "string", foreground: "86EFAC" },
                    { token: "number", foreground: "FCD34D" },
                    { token: "type", foreground: "93C5FD" },
                    { token: "identifier", foreground: "E7EAEF" },
                    { token: "delimiter", foreground: "A0A8B5" },
                  ],
                  colors: {
                    "editor.background": "#0F1115",
                    "editor.foreground": "#E7EAEF",
                    "editorLineNumber.foreground": "#3A414C",
                    "editorLineNumber.activeForeground": "#A0A8B5",
                    "editor.selectionBackground": "#2563EB55",
                    "editor.inactiveSelectionBackground": "#2563EB22",
                    "editorCursor.foreground": "#F59E0B",
                    "editor.lineHighlightBackground": "#14171D",
                    "editorIndentGuide.background": "#1A1D24",
                    "editorIndentGuide.activeBackground": "#2E3440",
                  },
                });
              }}
              onMount={(editor) => {
                editorRef.current = editor as unknown as MonacoEditorInstance;
                setEditor(editor as unknown as MonacoEditorInstance);
                editor.onDidChangeCursorPosition((e) => {
                  setCursor({ line: e.position.lineNumber, column: e.position.column });
                });
                // Cmd-S is now a no-op visual confirmation; persistence
                // happens server-side as edits stream over the WS.
              }}
              options={{
                readOnly: false,
                minimap: { enabled: false },
                fontFamily: "'JetBrains Mono', 'Menlo', Monaco, 'Courier New', monospace",
                fontSize: 12,
                lineNumbers: "on",
                folding: true,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                renderWhitespace: "selection",
                padding: { top: 12 },
              }}
            />
          </Suspense>
        )}
      </div>

      <StatusBar
        language={FRIENDLY_LANG[language] ?? language}
        line={cursor.line}
        column={cursor.column}
        coStatus={coStatus}
      />
    </div>
  );
}

function TabStrip({
  filename,
  fullPath,
  coStatus,
  onClose,
}: {
  filename: string;
  fullPath: string;
  coStatus: "idle" | "connecting" | "open" | "closed" | "error";
  onClose?: () => void;
}) {
  const indicator = coIndicator(coStatus);
  return (
    <div
      style={{
        display: "flex",
        background: "var(--bg-panel)",
        borderBottom: "1px solid var(--border-default)",
        height: 34,
        paddingLeft: 4,
        alignItems: "stretch",
        flexShrink: 0,
      }}
    >
      <div
        title={fullPath}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "0 10px",
          background: "#0F1115",
          borderRight: "1px solid var(--border-default)",
          borderTop: "1px solid var(--accent-blue)",
          color: "var(--text-primary)",
          fontSize: 12,
          fontFamily: "var(--font-sans)",
          marginTop: -1,
        }}
      >
        <FileIcon filename={filename} />
        <span style={{ whiteSpace: "nowrap" }}>{filename}</span>
        <span
          aria-label={indicator.label}
          title={indicator.label}
          style={{
            display: "inline-block",
            width: 7,
            height: 7,
            borderRadius: 4,
            background: indicator.color,
          }}
        />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close tab"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
            }}
          >
            <IconX />
          </button>
        )}
      </div>
    </div>
  );
}

function StatusBar({
  language,
  line,
  column,
  coStatus,
}: {
  language: string;
  line: number;
  column: number;
  coStatus: "idle" | "connecting" | "open" | "closed" | "error";
}) {
  const indicator = coIndicator(coStatus);

  return (
    <footer
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        height: 24,
        borderTop: "1px solid var(--border-default)",
        background: "var(--bg-panel)",
        padding: "0 12px",
        fontSize: 11,
        color: "var(--text-tertiary)",
        fontFamily: "var(--font-mono)",
        flexShrink: 0,
      }}
    >
      <span>{language}</span>
      <span style={{ color: indicator.color }}>{indicator.label}</span>
      <span style={{ flex: 1 }} />
      <span>
        Ln {line}, Col {column}
      </span>
      <span>Spaces: 2</span>
      <span>History</span>
    </footer>
  );
}

function coIndicator(state: "idle" | "connecting" | "open" | "closed" | "error"): { label: string; color: string } {
  switch (state) {
    case "open":
      return { label: "● live", color: "var(--accent-green)" };
    case "connecting":
      return { label: "connecting…", color: "var(--accent-blue)" };
    case "closed":
      return { label: "● reconnecting…", color: "var(--accent-amber)" };
    case "error":
      return { label: "co-edit error", color: "var(--accent-red)" };
    default:
      return { label: "idle", color: "var(--text-tertiary)" };
  }
}

function Message({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        padding: 16,
        color: error ? "var(--accent-red)" : "var(--text-tertiary)",
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

function FileIcon({ filename }: { filename: string }) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const color =
    ext === "ts" || ext === "tsx"
      ? "#3178c6"
      : ext === "js" || ext === "jsx"
        ? "#f7df1e"
        : ext === "json"
          ? "#fbbf24"
          : ext === "yaml" || ext === "yml"
            ? "#a855f7"
            : ext === "md"
              ? "#60a5fa"
              : "#A0A8B5";
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 2,
        background: color,
        flexShrink: 0,
      }}
    />
  );
}
